import React, { useState } from "react";
import { Badge } from "../../components/ui/badge";
import {
  Pencil,
  CheckCircle2,
} from "lucide-react";
import { useDebugMode } from "../../shared/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import EditPanel from "../../components/ui/EditPanel";
import { analytics } from "../../analytics";
import { useTranslation } from "react-i18next";




const ImprovementSection = ({
  label,
  oldValue,
  newValue,
  improvement,
  fieldName,
  improvements = [],
  onEdit,
  scoreComponents,
  imageUrl,
  productId,
  jobId,
  sourceId,
  forceShow = false,
}: {
  label: string;
  oldValue?: string;
  fieldName: string;
  sourceId?: string;
  newValue: string;
  jobId: string;
  productId: string;
  improvement: number;
  imageUrl?: string;
  improvements?: string[];
  onEdit?: (value: string) => Promise<void>;
  scoreComponents?: Record<string, number>; // Generic type for any object with string keys and number values
  forceShow?: boolean;
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const hasChanges = normalizeHtml(oldValue ?? "") !== normalizeHtml(newValue);
  const showEdit = hasChanges && onEdit;
  const isDebugMode = useDebugMode();
  const { enrichmentPendingChanges } = useSelector(
    (state: RootState) => state.flow
  );
  const { currentUser } = useSelector((state: RootState) => state.user);

  const visibleValue =
    enrichmentPendingChanges.find(
      (change) =>
        change.enrichmentJobId === jobId &&
        change.productId === productId &&
        change.fieldName === fieldName &&
        (!sourceId || change.sourceId === sourceId)
    )?.fieldValue ?? newValue;

  // Modify the visibility condition
  if (!forceShow && oldValue === newValue) {
    return null;
  }

  return (
    <div className="space-y-2">
      {imageUrl && (
        <div className="my-4">
          <img
            src={imageUrl}
            alt={label}
            className="w-24 h-24 object-cover rounded-lg"
          />
        </div>
      )}
      <div className="flex items-center justify-between">
        {fieldName !== "alt_text" && <h3 className="text-sm font-medium text-gray-700">{label}</h3>}

        {hasChanges && fieldName !== "alt_text" && improvement > 0 && (
          <Badge
            className="bg-green-50 text-green-700 hover:bg-green-50"
          >
            +{improvement}%
          </Badge>
        )}
      </div>
      <div className="space-y-4">
        {/* Content Evolution Display */}
        <div className="relative">
          <div
            className={`grid ${hasChanges ? "grid-cols-2" : "grid-cols-1"
              } gap-4`}
          >
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
              <div className="text-xs font-medium text-gray-400 mb-2">
                {t("improvementSection.original_version")}
              </div>
              <div
                className="text-sm text-gray-600 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: oldValue || "" }}
              />
            </div>

            {hasChanges && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="text-xs font-medium text-blue-500 mb-2">
                  {t("improvementSection.enhanced_version")}
                </div>
                <div
                  className="text-sm text-gray-900 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: visibleValue }}
                />

                {isDebugMode &&
                  scoreComponents && ( // Change this line
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="text-xs font-medium text-gray-500 mb-1">
                        {t("improvementSection.score_breakdown")}:
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {Object.entries(scoreComponents).map(
                          ([key, value], index) => (
                            <div
                              key={index}
                              className="flex justify-between text-gray-500"
                            >
                              <span>{key.split("_").join(" ")}:</span>
                              <span>{value}</span>
                            </div>
                          )
                        )}
                        <div className="flex justify-between text-gray-700 font-medium col-span-2 pt-2 mt-1 border-t">
                          <span>{t("improvementSection.total_score")}:</span>
                          <span>
                            {Object.values(scoreComponents).reduce(
                              (sum, value) => sum + value,
                              0
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            )}
          </div>

          {showEdit && (
            <button
              onClick={() => setIsEditing(true)}
              className="absolute -right-2 -top-2 p-1.5 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors shadow-sm"
              aria-label={t("improvementSection.edit")}
            >
              <Pencil className="w-4 h-4 text-gray-400" />
            </button>
          )}
        </div>

        {hasChanges && improvements.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {improvements.map((improvement, idx) => (
              <div
                key={idx}
                className="flex items-center space-x-1 text-xs text-blue-700 bg-blue-50/50 rounded-full px-2 py-0.5"
              >
                <CheckCircle2 className="h-3 w-3" />
                <span>{improvement}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {isEditing && (
        <EditPanel
          value={visibleValue}
          onSave={async (newValue) => {
            await onEdit?.(newValue);
            setIsEditing(false);
            analytics.sendEnhancementJobResultsEditedEvent({
              jobId: jobId,
              email: currentUser?.email ?? "unknown",
            })
          }}
          onCancel={() => setIsEditing(false)}
        />
      )}
    </div>
  );
};

const normalizeHtml = (html: string): string => {
  if (!html) return "";
  return html
    .replace(/>\s+</g, "><") // Remove whitespace between tags
    .replace(/\s+/g, " ") // Replace multiple spaces with single space
    .trim(); // Remove leading/trailing whitespace
};

export default ImprovementSection;